/**
 * @duffcloudservices/cms
 *
 * Vue 3 composables and Vite plugins for DCS CMS integration.
 *
 * @example Basic usage
 * ```vue
 * <script setup lang="ts">
 * import { useTextContent, useSEO } from '@duffcloudservices/cms'
 *
 * const { t } = useTextContent({
 *   pageSlug: 'home',
 *   defaults: {
 *     'hero.title': 'Welcome',
 *     'hero.subtitle': 'Build amazing things'
 *   }
 * })
 *
 * const { applyHead } = useSEO('home')
 * applyHead()
 * </script>
 *
 * <template>
 *   <h1>{{ t('hero.title') }}</h1>
 *   <p>{{ t('hero.subtitle') }}</p>
 * </template>
 * ```
 *
 * @example Vite configuration
 * ```typescript
 * // vite.config.ts
 * import { dcsContentPlugin, dcsSeoPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   plugins: [
 *     dcsContentPlugin(),
 *     dcsSeoPlugin()
 *   ]
 * })
 * ```
 *
 * @packageDocumentation
 */

// ---------------------------------------------------------------------------
// SIDE EFFECT ON IMPORT — fleet conversion capture (C-326).
//
// This is the only side effect the package has, and it is deliberate. Conversion
// capture existed here as an opt-in for months and was adopted by zero of eleven
// fleet sites, so on 2026-07-26 two reviews independently measured the same hole:
// Bryan's five tel: CTAs — the site's only conversion action — emitted nothing,
// and Kim Duff Homes had 6 pageViews in a year. Making it opt-in again would
// re-create exactly that.
//
// It is safe to run unconditionally: `installConversionCapture` declines on the
// server, inside the editor/preview iframe, under Do Not Track, and when the page
// opts out — and with no analytics transport present it binds one passive listener
// and buffers at most 50 events. It mutates no DOM and cannot throw into a click.
//
// A site needing per-site booking hosts calls `installConversionCapture({...})`
// itself at app entry; the first call wins and this one then no-ops.
//
// DO NOT add `"sideEffects": false` to this package's package.json — it would let a
// consumer's bundler drop this line and silently restore the blind spot.
// ---------------------------------------------------------------------------
import { installConversionCapture } from './conversionAutoInstall'

installConversionCapture()

// Composables
export { useTextContent } from './composables/useTextContent'
export { useSEO, createSiteSEO } from './composables/useSEO'

// Framework-agnostic SEO resolver + build-time head-tag helpers.
// Shared by the runtime composable and the build-time static-HTML emitter.
export {
  buildHeadTags,
  resolvePageSeo,
  generateJsonLd,
  generateOpenGraphMeta,
  generateTwitterMeta,
  spliceHeadHtml,
  renderHeadTags,
  stripManagedHeadTags,
  escapeJsonLd,
  // The SINGLE definition of "the managed set", shared by the build sink
  // (`stripManagedHeadTags`) and the runtime sweep, so the two cannot drift.
  isManagedMetaTag,
  isManagedLinkRel,
  // C-357 clause 4 — router-level head re-assert (the coverage half of the
  // head-authority contract). Adoption is one line at app entry:
  //   installSeoHead(router, { head })
  installSeoHead,
  normalizeSeoHeadPath,
  buildSeoHeadRouteMap,
  createSeoTransformPageData,
  buildVitePressSeoHead,
  defaultRelativePathToRoute,
  buildSitemapXml,
  buildRobotsTxt,
  buildLlmsTxt,
  isRouteIndexable,
  AI_BOTS,
  // Schema-completeness builders (shared @graph spine + Breadcrumb/Blog/FAQ +
  // honest Review/aggregateRating).
  buildGlobalGraph,
  buildBreadcrumbList,
  breadcrumbTrailFromRoute,
  buildBlogPosting,
  buildFaqPage,
  buildReviewSchemaParts,
  filterRealReviews,
  filterRealFaq,
  findReviewItemsForPage,
  absolutizeUrl,
  slugToTitle,
  type ResolvedHeadTags,
  type HeadMetaTag,
  type HeadLinkTag,
  type HeadScriptTag,
  type HeadTagOverrides,
  type InstallSeoHeadOptions,
  type SeoHeadRouterLike,
  type SeoHeadRouteLike,
  type SeoHeadClientLike,
  type SeoHeadEntryLike,
  type SeoHeadInput,
  type SeoHeadPageRoute,
  type SeoHeadPagesManifest,
  type SeoHeadResolution,
  type SeoHeadResolutionReason,
  type CreateSeoTransformPageDataOptions,
  type SeoPageContext,
  type SeoPageTypeRule,
  type ResolvedPageOverrides,
  type VitePressPageData,
  type VitePressHeadConfig,
  type BuildSitemapParams,
  type BuildRobotsParams,
  type BuildLlmsParams,
  type DcsRobotsOptions,
  type SchemaObject,
  type BreadcrumbCrumb,
  type BlogMeta,
  type ReviewSource,
  type FaqSource,
  type ContentConfig,
} from './seo'
export { useReleaseNotes } from './composables/useReleaseNotes'
export { useSiteVersion } from './composables/useSiteVersion'
export { useMediaCarousel } from './composables/useMediaCarousel'
export { useResponsiveImage } from './composables/useResponsiveImage'
export {
  useReviewContent,
  type ReviewItem,
  type UseReviewContentConfig,
  type UseReviewContentReturn,
} from './composables/useReviewContent'
// Conversion-click capture: one delegated listener that measures outbound booking
// links, tel:/sms:/mailto: taps, managed form submits and social clicks. Deliberately
// SDK-agnostic and buffered so it survives an App Insights deferral (fleet P3) — see the
// module header.
export {
  useConversionTracking,
  createConversionTracker,
  attachConversionSink,
  classifyHref,
  redactHref,
  hashTarget,
  hrefScheme,
  isSubmitControl,
  isConversionType,
  doNotTrackEnabled,
  CONVERSION_CAPTURE_VERSION,
  CONVERSION_INTERACTION_TYPES,
  DEFAULT_BOOKING_HOSTS,
  DEFAULT_SOCIAL_HOSTS,
  type ConversionEvent,
  type ConversionSink,
  type ConversionTracker,
  type ConversionTrackingOptions,
  type ConversionInteractionType,
} from './composables/useConversionTracking'
export {
  installConversionCapture,
  installedConversionTracker,
  resetConversionCapture,
  conversionOptedOut,
  inFrame,
  type InstallConversionCaptureOptions,
  type ConversionInstallResult,
  type ConversionInstallSkipReason,
} from './conversionAutoInstall'
export {
  useSiteVisitorSession,
  fetchSiteVisitorSession,
  // Payload-level session gate for sites that own their own fetch. An anonymous visitor
  // is `200 {"visitor":null}` (A-35), so "the request succeeded" is NOT "signed in" —
  // these two are what a hand-rolled store adopts instead of re-deriving that (C-619).
  readSiteVisitorSessionResponse,
  siteVisitorFromSessionPayload,
  type SiteVisitor,
  type SiteVisitorSessionResult,
  type FetchSiteVisitorSessionOptions,
  type UseSiteVisitorSessionOptions,
  type UseSiteVisitorSessionReturn,
} from './composables/useSiteVisitorSession'

// Note: Vue SFC components (ResponsiveImage, PreviewRibbon, LiteMediaEmbed) are
// exported via the package.json "exports" map pointing to raw .vue source files.
// Import them as:
//   import { ResponsiveImage } from '@duffcloudservices/cms/responsive-image'
//   import { PreviewRibbon } from '@duffcloudservices/cms/components'
//   import LiteMediaEmbed from '@duffcloudservices/cms/lite-media-embed'
// LiteMediaEmbed is the default click-to-load facade for `useMediaCarousel`
// youtube/instagram items (poster first, iframe only on user interaction).

// Types
export type {
  // Content types
  DcsContentFile,
  TextContentConfig,
  TextContentReturn,
  // Media carousel types
  MediaCarouselItem,
  UseMediaCarouselConfig,
  UseMediaCarouselReturn,
  // SEO types
  SeoConfiguration,
  GlobalSeoConfig,
  PageSeoConfig,
  SeoAuthorConfig,
  SeoSocialConfig,
  SeoImagesConfig,
  SeoOpenGraphConfig,
  SeoTwitterConfig,
  SeoSchemaConfig,
  SeoAlternateConfig,
  SeoVerificationConfig,
  ResolvedPageSeo,
  UseSeoConfig,
  UseSeoReturn,
  HeadOverrides,
  // Release notes types
  ReleaseNote,
  ReleaseNotesReturn,
  SiteVersionReturn,
} from './types'

// Re-export responsive image types from core for convenience
export type {
  ImageContext,
  ResponsiveImageOptions,
  ResponsiveSource,
  ResponsiveImageResult,
} from '@duffcloudservices/cms-core'

export { resolveResponsiveImage, isCdnAssetUrl } from '@duffcloudservices/cms-core'

// ---------------------------------------------------------------------------
// Synthetic-capture detection (C-614).
//
// The shared predicate a site's telemetry boot calls to decline an automated
// capture pass BEFORE loading its analytics SDK. `installConversionCapture`
// already consumes it internally, so conversion capture is covered by importing
// this package at all; a site's pageView boot is site-local code and adopts it
// with one line. See ./syntheticCapture.ts for the measurement that motivated it.
// ---------------------------------------------------------------------------
export {
  SYNTHETIC_CAPTURE_PARAMS,
  syntheticCaptureDetected,
  syntheticCaptureMarker,
} from './syntheticCapture'
export type { SyntheticCaptureMarker } from './syntheticCapture'
